<template>
  <div>
    <pdf-preview
      ref="pdfPreview"
      :saving="saving"
      :formData="formData"
      @in-preview="inPreview = $event"
      @save="save"
      @save-preview="save(true)"
    />
    <div v-show="!inPreview">
      <form autocomplete="off" @submit.prevent :key="draftKey">
        <h2>FORMULARIO INICIAL DE SOLICITUD DE ADQUISICIÓN DE BIENES O SERVICIOS</h2>
        <div class="row mt-10">
          <div class="col-12">
            <b-form-group>
              <label for="formType">
                ¿Deseas basarte en otro documento?
              </label>
              <v-select
                v-model="previousDocument"
                :options="createdDocuments"
                name="previousDocument"
                label="label"
                data-cy="created-document-data"
                @input="getDocumentData($event)"
                :clearable="true"
              >
                <template slot="no-options">
                  no hay documentos registrados
                </template>
                <template #list-footer v-if="hasMoreDocuments">
                  <li class="text-center py-2" style="border-top: 1px solid #e8e8e8;">
                    <a
                      href="#"
                      @click.prevent="loadMoreDocuments"
                      style="color: #7367f0; font-weight: 500;"
                    >
                      {{ loadingMoreDocuments ? 'Cargando...' : 'Cargar más documentos' }}
                    </a>
                  </li>
                </template>
              </v-select>
              <v-invalid-feedback v-if="$validator.errors.has('formType')">
                {{ $validator.errors.first('formType') }}
              </v-invalid-feedback>
            </b-form-group>
          </div>
          <div class="col-12">
            <h4>IDENTIFICACIÓN DEL REQUIRENTE</h4>
          </div>
          <div class="col-12">
            <validate-field
              field="requesterUnit"
              label="Unidad requirente"
              v-slot="{ invalid }"
            >
              <select-options
                name="requesterUnit"
                :alphabetical-sort="true"
                options-group="fisabs-requester-unit"
                v-model="formData.requesterUnit"
                data-vv-as="unidad requirente"
                data-cy="requester-unit"
                v-validate="'required'"
                :invalid="invalid"
              />
            </validate-field>
          </div>
          <div class="col-12">
            <validate-field
              field="requesterName"
              label="Responsable de elaborar la solicitud"
              v-slot="{ invalid }"
            >
              <b-input
                name="requesterName"
                placeholder="Ingrese el nombre del responsable de elaborar la solicitud"
                v-model="formData.requesterName"
                data-vv-as="responsable de elaborar la solicitud"
                data-cy="requester-name"
                v-validate="'required'"
                :state="invalid"
              />
            </validate-field>
          </div>
          <div class="col-12">
            <validate-field
              field="approverBossName"
              label="Nombre del jefe/a que aprueba la solicitud"
              v-slot="{ invalid }"
            >
              <select-options
                name="approverBossName"
                options-group="fisabs-approver-boss-name"
                :alphabetical-sort="true"
                v-model="formData.approverBossName"
                data-vv-as="nombre del jefe/a que aprueba la solicitud"
                data-cy="approver-boss-name"
                v-validate="'required'"
                :invalid="invalid"
              />
            </validate-field>
          </div>
          <div class="col-12">
            <validate-field
              field="approverBossPosition"
              label="Cargo del jefe/a que aprueba la solicitud"
              v-slot="{ invalid }"
            >
              <select-options
                name="approverBossPosition"
                options-group="fisabs-approver-boss-position"
                :alphabetical-sort="true"
                v-model="formData.approverBossPosition"
                data-vv-as="cargo del jefe/a que aprueba la solicitud"
                data-cy="approver-boss-position"
                v-validate="'required'"
                :invalid="invalid"
              />
            </validate-field>
          </div>
          <div class="col-12">
            <validate-field
              field="contractMonitoringOfficer"
              label="Encargado de seguimiento contractual"
              v-slot="{ invalid }"
            >
              <select-options
                name="contractMonitoringOfficer"
                options-group="fisabs-contract-monitoring-officer"
                :alphabetical-sort="true"
                v-model="formData.contractMonitoringOfficer"
                data-vv-as="encargado de seguimiento contractual"
                data-cy="contract-monitoring-officer"
                v-validate="'required'"
                :invalid="invalid"
              />
            </validate-field>
          </div>
          <div class="col-12 mt-5">
            <h4>
              ANTECEDENTES PRESUPUESTARIOS GENERALES
            </h4>
          </div>
          <div class="col-12 mb-4">
            <validate-field
              field="cdpNumber"
              label="N° CDP"
            >
              <b-input
                name="cdpNumber"
                placeholder="Ingrese el número de CDP"
                v-model="formData.cdpNumber"
                data-cy="cdp-number"
              />
            </validate-field>
            <small class="italic">(Se debe adjuntar certificado de disponibilidad presupuestaria
              debidamente tramitado por la unidad de finanzas al final del formulario)</small>
          </div>

          <div class="col-12">
            <validate-field
              field="serviceCostCenter"
              label="Centro de Costo"
              v-slot="{ invalid }"
            >
              <select-options
                name="serviceCostCenter"
                options-group="fisabs-service-cost-center"
                v-model="formData.serviceCostCenter"
                data-vv-as="centro de costo"
                data-cy="service-cost-center"
                v-validate="'required'"
                :invalid="invalid"
              />
            </validate-field>
          </div>
          <div class="col-12">
            <validate-field
              field="serviceFundingSource"
              label="Fuente de financiamiento"
              v-slot="{ invalid }"
            >
              <select-options
                name="serviceFundingSource"
                options-group="fisabs-service-funding-source"
                v-model="formData.serviceFundingSource"
                data-vv-as="fuente de financiamiento"
                data-cy="service-funding-source"
                v-validate="'required'"
                :invalid="invalid"
              />
            </validate-field>
          </div>
        </div>
        <vs-divider/>
        <div class="row my-5">
          <div class="col-12">
            <h4>SERVICIOS O BIENES REQUERIDOS</h4>
          </div>
        </div>
        <list-structure
          :items="formData.items"
          :default-item="initialItem"
          :min-items="1"
          @add-item="onAddItem"
        >
          <template v-slot="{ id, item }">
            <div class="row">
              <div class="col-12">
                <h4>Información del bien o servicio</h4>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'budgetAccount-' + id"
                  label="Cuenta presupuestaria"
                  v-slot="{ invalid }"
                >
                  <select-options
                    placeholder="Cuenta contable asignada a este tipo de gasto según ERP."
                    :name="'budgetAccount-' + id"
                    options-group="fisabs-budget-accounts"
                    v-model="item.budgetAccount"
                    data-vv-as="cuenta presupuestaria"
                    data-cy="budget-account"
                    v-validate="'required'"
                    :invalid="invalid"
                  />
                </validate-field>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'itemCatalogue-' + id"
                  label="Catálogo de bienes o servicios (ERP Global)"
                  v-slot="{ invalid }"
                >
                  <select-options-paginated
                    placeholder="Debe hacer match con el código o nombre del ítem en ERP Global."
                    :name="'itemCatalogue-' + id"
                    options-group="fisabs-item-catalogue-erp"
                    v-model="item.itemCatalogue"
                    :clearable="true"
                    data-vv-as="catálogo de bienes o servicios (ERP Global)"
                    data-cy="item-catalogue"
                    v-validate="'required'"
                    :invalid="invalid"
                  />
                </validate-field>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'requiredServiceName-' + id"
                  label="Nombre del bien o servicio requerido"
                  v-slot="{ invalid }"
                >
                  <b-input
                    :name="'requiredServiceName-' + id"
                    placeholder="Ingrese el nombre del bien o servicio requerido"
                    v-model="item.requiredServiceName"
                    data-vv-as="nombre del bien o servicio requerido"
                    data-cy="required-service-name"
                    v-validate="'required'"
                    :state="invalid"
                  />
                </validate-field>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'serviceStartDate-' + id"
                  label="Fecha de inicio del bien o servicio"
                  v-slot="{ invalid }"
                >
                  <date-picker
                    :name="'serviceStartDate-' + id"
                    v-model="item.serviceStartDate"
                    placeholder="Seleccione la fecha de inicio del bien o servicio"
                    data-vv-as="fecha de inicio del bien o servicio"
                    data-cy="service-start-date"
                    format="DD-MM-YYYY"
                    value-type="DD-MM-YYYY"
                    v-validate="'required'"
                    :invalid="invalid"
                    @formatted-input="item.serviceStartDate = $event"
                    :disablePastDates="true"
                  />
                </validate-field>
              </div>
            </div>
            <div class="row">
              <div
                :class="item.serviceDeliveryType === 'Servicio en el tiempo'
                  ? `col-md-6 col-12` : `col-12`"
              >
                <validate-field
                  :field="'serviceDeliveryType-' + id"
                  label="Tipo de entrega del bien o servicio"
                  v-slot="{ invalid }"
                >
                  <select-options
                    :name="'serviceDeliveryType-' + id"
                    v-model="item.serviceDeliveryType"
                    data-vv-as="tipo de entrega del bien o servicio"
                    data-cy="service-delivery-type"
                    v-validate="'required'"
                    :invalid="invalid"
                    :mapper="opt => ({ value: opt, label: opt })"
                    :options="options.serviceDeliveryType"
                    @input="item.serviceDeliveryFrequency = ''"
                  />
                </validate-field>
              </div>
              <div
                class="col-md-6 col-12"
                v-if="item.serviceDeliveryType === 'Servicio en el tiempo'"
              >
                <validate-field
                  :field="'serviceDeliveryFrequency-' + id"
                  label="Frecuencia de entrega"
                  v-slot="{ invalid }"
                >
                  <select-options
                    :name="'serviceDeliveryFrequency-' + id"
                    options-group="fisabs-service-delivery-frequency"
                    :alphabetical-sort="true"
                    v-model="item.serviceDeliveryFrequency"
                    data-vv-as="frecuencia de entrega"
                    data-cy="service-delivery-frequency"
                    v-validate="'required'"
                    :invalid="invalid"
                  />
                </validate-field>
              </div>
              <div
                class="col-12"
                v-if="item.serviceDeliveryType === 'Servicio en el tiempo'"
              >
                <validate-field
                  :field="'serviceDeliveryPeriodicity-' + id"
                  label="Periodicidad de entrega"
                  v-slot="{ invalid }"
                >
                  <date-picker
                    :name="'serviceDeliveryPeriodicity-' + id"
                    v-model="item.serviceDeliveryPeriodicity"
                    placeholder="Seleccione la periodicidad de entrega"
                    data-vv-as="periodicidad de entrega"
                    data-cy="service-delivery-periodicity"
                    v-validate="'required'"
                    :invalid="invalid"
                    multiple
                    @formatted-input="item.serviceDeliveryPeriodicity = $event"
                    :disablePastDates="true"
                  />
                </validate-field>
              </div>
            </div>
            <div class="row">
              <div
                :class="item.serviceDeliveryLocation === OTHER_ADDRESS
                  ? `col-md-6 col-12` : `col-12`"
              >
                <validate-field
                  :field="'serviceDeliveryLocation-' + id"
                  label="Dirección de despacho del bien o servicio"
                  v-slot="{ invalid }"
                >
                  <select-options
                    :name="'serviceDeliveryLocation-' + id"
                    v-model="item.serviceDeliveryLocation"
                    data-vv-as="dirección de despacho del bien o servicio"
                    data-cy="service-delivery-location"
                    v-validate="'required'"
                    :invalid="invalid"
                    options-group="fisabs-service-delivery-location"
                    @input="(value) => handleLocationChange(value, item)"
                  />
                </validate-field>
              </div>
              <div
                class="col-md-6 col-12"
                v-if="item.serviceDeliveryLocation === OTHER_ADDRESS"
              >
                <validate-field
                  :field="'serviceDeliveryLocationCustom-' + id"
                  label="Dirección y oficina a la que corresponde"
                  v-slot="{ invalid }"
                >
                  <b-input
                    :name="'serviceDeliveryLocationCustom-' + id"
                    placeholder="Ingrese la dirección y oficina a la que corresponde"
                    v-model="item.serviceDeliveryLocationCustom"
                    data-vv-as="dirección y oficina a la que corresponde"
                    data-cy="service-delivery-location-custom"
                    v-validate="'required'"
                    :state="invalid"
                  />
                </validate-field>
              </div>
              <div
                class="col-12"
                v-if="item.serviceDeliveryLocation &&
                item.serviceDeliveryLocation !== DEFAULT_ADDRESS"
              >
                <validate-field
                  :field="'serviceDeliveryLocationCustom-' + id"
                  label="Justificación"
                  v-slot="{ invalid }"
                >
                  <b-input
                    :name="'serviceDeliveryLocationJustification-' + id"
                    placeholder="Ingrese la justificación de otra dirección"
                    v-model="item.serviceDeliveryLocationJustification"
                    data-vv-as="justificación"
                    data-cy="service-delivery-location-justification"
                    v-validate="'required'"
                    :state="invalid"
                  />
                </validate-field>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <vs-divider/>
              </div>
              <div class="col-12 mb-2 ">
                <link-button
                  class="mb-2"
                  title="Catálogo ERP Global"
                  :link="item.linkCatalogoErpGlobal"
                />
              </div>
              <div class="col-12">
                <link-button
                  class="mb-2"
                  title="Ir a catálogo de convenio marco"
                  link="https://www.mercadopublico.cl/TiendaHome/"
                />
                <validate-field
                  :field="'catalogAvailability-' + id"
                  label="Disponibilidad en catálogo de convenio marco."
                  v-slot="{ invalid }"
                >
                  <select-options
                    :name="'catalogAvailability-' + id"
                    v-model="item.catalogAvailability"
                    data-vv-as="disponibilidad en catálogo de convenio marco"
                    data-cy="catalog-availability"
                    v-validate="'required'"
                    :invalid="invalid"
                    :options="options.yesOrNo"
                    :mapper="opt => ({ value: opt, label: opt })"
                  />
                </validate-field>
              </div>
              <div class="col-12">
                <vs-divider class="mt-0"/>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'criteriaToSelectProvider-' + id"
                  label="Criterios que se deben considerar para seleccionar proveedor"
                  v-slot="{ invalid }"
                >
                  <select-options
                    :name="'criteriaToSelectProvider-' + id"
                    v-model="item.criteriaToSelectProvider"
                    data-vv-as="criterios que se deben considerar para seleccionar proveedor"
                    data-cy="criteria-to-select-provider"
                    v-validate="'required'"
                    :invalid="invalid"
                    :options="options.criteriaToSelectProvider"
                    :mapper="opt => ({ value: opt, label: opt })"
                    @input="item.providerSelectionCriteria = ''"
                    multiple
                  />
                </validate-field>
              </div>
              <div
                class="col-12"
                v-if="item.criteriaToSelectProvider
                  && (item.criteriaToSelectProvider.join().includes('Otro (detallar)'))
                "
              >
                <validate-field
                  :field="'providerSelectionCriteria-' + id"
                  label="Detalle del criterio de selección de proveedor"
                  v-slot="{ invalid }"
                >
                  <b-input
                    :name="'providerSelectionCriteria-' + id"
                    placeholder="Ingrese el detalle del criterio de selección de proveedor"
                    v-model="item.providerSelectionCriteria"
                    data-vv-as="detalle del criterio de selección de proveedor"
                    data-cy="provider-selection-criteria"
                    v-validate="'required'"
                    :state="invalid"
                  />
                </validate-field>
              </div>
              <div class="col-12">
                <h4>Detalle técnico y valor estimado</h4>
              </div>
              <div class="col-12 col-md-6">
                <validate-field
                  :field="'serviceQuantity-' + id"
                  label="Cantidad"
                  v-slot="{ invalid }"
                >
                  <b-input
                    type="number"
                    :name="'serviceQuantity-' + id"
                    placeholder="Ingrese la cantidad"
                    v-model="item.serviceQuantity"
                    data-vv-as="cantidad"
                    data-cy="service-quantity"
                    v-validate="'required'"
                    :state="invalid"
                    @input="calculateSubtotal(item)"
                  />
                </validate-field>
              </div>
              <div class="col-12 col-md-6">
                <validate-field
                  :field="'serviceMeasure-' + id"
                  label="Medida"
                  v-slot="{ invalid }"
                >
                  <select-options
                    :name="'serviceMeasure-' + id"
                    options-group="fisabs-service-measure"
                    :alphabetical-sort="true"
                    v-model="item.serviceMeasure"
                    data-vv-as="medida"
                    data-cy="service-measure"
                    v-validate="'required'"
                    :invalid="invalid"
                  />
                </validate-field>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'serviceSpecifications-' + id"
                  label="Especificaciones técnicas del bien o servicio"
                  v-slot="{ invalid }"
                >
                  <b-form-textarea
                    :name="'serviceSpecifications-' + id"
                    v-model="item.serviceSpecifications"
                    data-vv-as="especificaciones técnicas del bien o servicio"
                    data-cy="service-specifications"
                    v-validate="'required'"
                    :state="invalid"
                    :maxlength="2000"
                    placeholder="Ingrese las especificaciones técnicas del bien o servicio"
                    rows="3"
                    max-rows="8"
                  />
                  <small class="form-text text-muted text-right">
                    {{ item.serviceSpecifications ? item.serviceSpecifications.length : 0 }} / 2000
                  </small>
                </validate-field>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'serviceAmount-' + id"
                  label="Monto (CLP)"
                  v-slot="{ invalid }"
                >
                  <number-currency-input
                    :name="'serviceAmount-' + id"
                    v-model="item.serviceAmount"
                    data-vv-as="monto"
                    data-cy="service-amount"
                    v-validate="'required|minCurrencyValue:1'"
                    :state="invalid"
                    @input="calculateSubtotal(item)"
                  />
                </validate-field>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'subtotalAmount-' + id"
                  label="Subtotal (CLP)"
                  v-slot="{ invalid }"
                >
                  <b-input-group prepend="$">
                    <b-input
                      :name="'subtotalAmount-' + id"
                      v-model="item.subtotalAmount"
                      data-vv-as="subtotal"
                      data-cy="subtotal-amount"
                      :state="invalid"
                      readonly
                    />
                  </b-input-group>
                </validate-field>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'serviceGlosa-' + id"
                  label="Glosa (nombre del proyecto cuando corresponda)"
                >
                  <b-input
                    :name="'serviceGlosa-' + id"
                    placeholder="Ingrese la glosa"
                    v-model="item.serviceGlosa"
                    data-cy="service-glosa"
                  />
                </validate-field>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'serviceAgreementCode-' + id"
                  label="Código de convenio marco (solo si aplica)"
                >
                  <b-input
                    :name="'serviceAgreementCode-' + id"
                    placeholder="Ingrese el código de convenio marco"
                    v-model="item.serviceAgreementCode"
                    data-cy="service-agreement-code"
                  />
                </validate-field>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'observations-' + id"
                  label="Observaciones del ítem"
                  v-slot="{ invalid }"
                >
                  <b-form-textarea
                    :name="'observations-' + id"
                    v-model="item.observations"
                    data-vv-as="observaciones del ítem"
                    data-cy="service-observations"
                    :state="invalid"
                    :maxlength="2000"
                    placeholder="Ingrese observaciones complementarias del ítem"
                    rows="3"
                    max-rows="8"
                  />
                  <small class="form-text text-muted text-right">
                    {{ item.observations ? item.observations.length : 0 }} / 2000
                  </small>
                </validate-field>
              </div>
              <div class="col-12">
                <h4>Información relacionada a proyectos </h4>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'serviceInPurchasePlan-' + id"
                  label="¿Está en Plan de Gastos? (Solo para Proyectos)"
                  v-slot="{ invalid }"
                >
                  <select-options
                    :name="'serviceInPurchasePlan-' + id"
                    v-model="item.serviceInPurchasePlan"
                    data-vv-as="indicar si está en plan de gastos"
                    data-cy="service-in-purchase-plan"
                    v-validate="'required'"
                    :invalid="invalid"
                    :options="options.yesOrNo"
                    :mapper="opt => ({ value: opt, label: opt })"
                  />
                </validate-field>
              </div>
              <div class="col-12" v-if="item.serviceInPurchasePlan === 'No'">
                <validate-field
                  :field="'servicePurchasePlanJustification-' + id"
                  label="Justificación en caso de no estar en plan de gastos"
                  v-slot="{ invalid }"
                >
                  <b-input
                    :name="'servicePurchasePlanJustification-' + id"
                    placeholder="Ingrese la justificación"
                    v-model="item.servicePurchasePlanJustification"
                    data-vv-as="justificación en caso de no estar en plan de gastos"
                    data-cy="service-purchase-plan-justification"
                    v-validate="'required'"
                    :state="invalid"
                  />
                </validate-field>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'serviceExpenseClassification-' + id"
                  label="Clasificación del gasto (solo para proyecto)"
                >
                  <select-options
                    :name="'serviceExpenseClassification-' + id"
                    options-group="fisabs-service-expense-classification"
                    v-model="item.serviceExpenseClassification"
                    data-cy="service-expense-classification"
                    :alphabetical-sort=true
                    :filter-by-doc-type="DOC_TYPE"
                  />
                </validate-field>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'serviceProjectObjective-' + id"
                  label="Objetivo del proyecto"
                >
                  <b-input
                    :name="'serviceProjectObjective-' + id"
                    placeholder="Ingrese el objetivo del proyecto"
                    v-model="item.serviceProjectObjective"
                    data-cy="service-project-objective"
                  />
                </validate-field>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'serviceResult-' + id"
                  label="Resultado / Hito / Actividad principal / Indicador"
                >
                  <b-input
                    :name="'serviceResult-' + id"
                    placeholder="Ingrese el resultado/hito/actividad principal/indicador"
                    v-model="item.serviceResult"
                    data-cy="service-result"
                  />
                </validate-field>
              </div>
              <div class="col-12">
                <validate-field
                  :field="'serviceExpenseCode-' + id"
                  label="Código del gasto en el plan de gastos del proyecto"
                >
                  <b-input
                    :name="'serviceExpenseCode-' + id"
                    placeholder="Ingrese el código del gasto en el plan de gastos del proyecto"
                    v-model="item.serviceExpenseCode"
                    data-cy="service-expense-code"
                  />
                </validate-field>
              </div>
            </div>
          </template>
        </list-structure>
      </form>

      <vs-divider/>
      <div class="row">
        <div class="col-6">
          <b-form-group class="mt-0 mt-md-8">
            <b-form-checkbox
              name="initiatedFisabs"
              data-cy="initiated-fisabs"
              data-vv-as="¿Esta solicitud complementa un requerimiento ya iniciado?"
              v-model="formData.initiatedFisabs"
              @change="() => {
                formData.createdFisabsSelected = {};
                formData.fisabsSelected = '';
              }"
              class="m-0"
            >
              ¿Esta solicitud complementa un requerimiento ya iniciado?
            </b-form-checkbox>
          </b-form-group>
        </div>
        <div class="col-6" v-if="formData.initiatedFisabs">
          <validate-field
            field="createdFisabsSelected"
            label="FISABS anterior asociado"
            v-slot="{ invalid }"
          >
            <v-select
              v-model="formData.fisabsSelected"
              :options="createdFisabs"
              name="createdFisabsSelected"
              label="label"
              data-vv-as="FISABS anterior asociado"
              data-cy="created-fisabs-selected"
              v-validate="{required: formData.initiatedFisabs}"
              :reduce="option => option.id"
              @input="formData.createdFisabsSelected = createdFisabs
              .find(elem => elem.id === $event)"
              :clearable="true"
              :class="{'v-select-danger': invalid === false}"
            >
              <template slot="no-options">
                no hay FISABS registrados
              </template>
              <template #list-footer v-if="hasMoreFisabs">
                <li class="text-center py-2" style="border-top: 1px solid #e8e8e8;">
                  <a
                    href="#"
                    @click.prevent="loadMoreFisabs"
                    style="color: #7367f0; font-weight: 500;"
                  >
                    {{ loadingMoreFisabs ? 'Cargando...' : 'Cargar más FISABS' }}
                  </a>
                </li>
              </template>
            </v-select>
          </validate-field>
          <label></label>
        </div>
        <div class="col-12">
          <validate-field
            field="general-observations"
            label="Observaciones generales de la solicitud"
            v-slot="{ invalid }"
          >
            <b-form-textarea
              name="general-observations"
              v-model="formData.generalObservations"
              data-vv-as="observaciones generales de la solicitud"
              data-cy="general-observations"
              :state="invalid"
              :maxlength="2000"
              placeholder="Observaciones generales"
              rows="3"
              max-rows="8"
            />
            <small class="form-text text-muted text-right">
              {{ formData.generalObservations ? formData.generalObservations.length : 0 }} / 2000
            </small>
          </validate-field>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <div v-if="dfsSelectorsLoading || dfsSelectors.length" class="h2">Adjuntos</div>
          <dynamic-file-selector
            v-if="!dfsSelectorsLoading"
            :selectors="dfsSelectors"
            :object-to-evaluate="formData"
            :files="dfsFiles"
            :reset-key="dfsResetKey"
            @update-files="dfsUpdateFiles"
            @valid="(valid) => dfsValid = valid"
          />
          <div v-else>
            <b-skeleton animation="fade" width="100%"/>
            <b-skeleton animation="fade" width="100%"/>
          </div>
        </div>
      </div>
      <div class="text-xs text-right">
        <b>Los campos marcados con <span class="text-danger">*</span> son obligatorios</b>
      </div>
      <form-buttons
        :saving="saving"
        :disabled="dfsSelectorsLoading"
        :back="() => this.$router.back()"
        :preview="preview"
        :save="() => save(false)"
        :saveDraft="saveDraft"
      />
    </div>
  </div>
</template>
<script>
import Vue from 'vue';

import { DOCUMENT_TYPE } from './constants';

import { ioCreateCustomDocument, ioGetCustomDocument, ioGetDocuments } from '@/febos/servicios/api/customDocuments.api';
import { showToast } from '@/febos/global/customDocuments/utils/showToast';
import { showInvalidFormNotification } from '@/febos/global/customDocuments/utils/notifications';
import { scrollToNamedElement } from '@/febos/global/customDocuments/utils/scroll';
import { getAttachmentsConfig } from '@/febos/global/customDocuments/utils/attachments';
import PdfPreview from '@/febos/global/customDocuments/components/PdfPreview';
import FormButtons from '@/febos/global/customDocuments/components/FormButtons';
import SelectOptions from '@/febos/global/customDocuments/components/SelectOptions';
import ValidateField from '@/febos/global/customDocuments/components/ValidateField';
import NumberCurrencyInput from '@/febos/global/customDocuments/components/NumberCurrencyInput';
import LinkButton from '@/febos/global/customDocuments/components/LinkButton';
import DatePicker from '@/febos/global/customDocuments/components/DatePicker';
import DynamicFileSelector from '@/febos/global/customDocuments/components/dynamic-file-selector/DynamicFileSelector';
import ListStructure from '@/febos/global/customDocuments/components/ListStructure';
import SelectOptionsPaginated from '@/febos/global/customDocuments/components/SelectOptionsPaginated';
import { listV2Options } from '@/febos/servicios/api/opciones.api';
import HandleDraft from '@/febos/global/documentManagement/mixins/HandleDraft';
import VInvalidFeedback from "@/febos/global/customDocuments/components/VInvalidFeedback.vue";
import HandlePreLoadData from "@/febos/global/documentManagement/mixins/HandlePreLoadData";

const DOC_TYPE = DOCUMENT_TYPE.FISABS;
const OTHER_ADDRESS = 'Otra dirección';
const DEFAULT_ADDRESS = 'Eusebio Lillo 667, Coyhaique';

const INITIAL_ITEM = {
  linkCatalogoErpGlobal: '',
  requiredServiceName: '',
  serviceDeliveryType: '',
  serviceDeliveryFrequency: '',
  serviceDeliveryPeriodicity: [],
  serviceDeliveryPeriodicityFormatted: [],
  serviceDeliveryLocation: '',
  serviceDeliveryLocationCustom: '',
  serviceDeliveryLocationJustification: '',
  catalogAvailability: '',
  criteriaToSelectProvider: [],
  providerSelectionCriteria: '',
  itemCatalogue: '',
  budgetAccount: '',
  serviceQuantity: '',
  serviceMeasure: '',
  serviceSpecifications: '',
  serviceAmount: '',
  subtotalAmount: '',
  serviceGlosa: '',
  serviceAgreementCode: '',
  serviceCostCenter: '',
  serviceFundingSource: '',
  serviceInPurchasePlan: '',
  servicePurchasePlanJustification: '',
  serviceExpenseClassification: '',
  serviceProjectObjective: '',
  serviceResult: '',
  serviceExpenseCode: '',
};

export default {
  name: DOC_TYPE,
  components: {
    VInvalidFeedback,
    SelectOptionsPaginated,
    ListStructure,
    DynamicFileSelector,
    PdfPreview,
    FormButtons,
    ValidateField,
    NumberCurrencyInput,
    SelectOptions,
    LinkButton,
    DatePicker,
  },
  mixins: [HandleDraft, HandlePreLoadData],
  data() {
    return {
      DEFAULT_ADDRESS,
      OTHER_ADDRESS,
      DOC_TYPE,
      usr: {},
      options: {
        yesOrNo: ['Sí', 'No'],
        serviceDeliveryType: ['Entrega inmediata', 'Servicio en el tiempo'],
        criteriaToSelectProvider: ['Precio', 'Menor plazo ofertado', 'Otro (detallar)'],
      },
      initialItem: { ...INITIAL_ITEM },
      formData: {
        fisabsSelected: '',
        createdFisabsSelected: {},
        serviceCostCenter: '',
        serviceFundingSource: '',
        requesterUnit: '',
        requesterName: '',
        requesterEmail: '',
        approverBossName: '',
        approverBossPosition: '',
        contractMonitoringOfficer: '',
        initiatedFisabs: false,
        cdpNumber: '',
        items: [],
        linked: [],
        attachments: [],
        generalObservations: '',
      },
      createdFisabs: [],
      fisabsPage: 1,
      hasMoreFisabs: false,
      loadingMoreFisabs: false,
      saving: false,
      inPreview: false,

      dfsValid: true,
      dfsSelectors: [],
      dfsSelectorsLoading: true,
      dfsFiles: [],
      linkCatalogoErpGlobal: ''
    };
  },
  methods: {
    async validateForm() {
      this.$validator.errors.clear();
      const fieldsValid = await this.$validator.validateAll();
      const attachmentsValid = (this.dfsValid || !this.dfsSelectors.length);

      const valid = fieldsValid && attachmentsValid;

      if (!valid) {
        showInvalidFormNotification(this);
        scrollToNamedElement(this.$validator?.errors?.items[0]?.field);
      }

      return valid;
    },
    getFormData() {
      const formatCurrency = (value) => `$${ Number(value).toLocaleString('es-CL')}`;
      this.formData.items.forEach((item) => {
        if (item.serviceDeliveryPeriodicity && item.serviceDeliveryPeriodicity.length > 0) {
          const fechasNormalizadas = item.serviceDeliveryPeriodicity.map((fecha) => {
            const partes = fecha.split('-');
            if (partes[0].length === 4) {
              // yyyy-mm-dd
              return `${partes[0]}-${partes[1].padStart(2, '0')}-${partes[2].padStart(2, '0')}`;
            }
            // dd-mm-yyyy
            return `${partes[2]}-${partes[1].padStart(2, '0')}-${partes[0].padStart(2, '0')}`;
          });
          // Ordenar
          fechasNormalizadas.sort((a, b) => new Date(a) - new Date(b));
          if (fechasNormalizadas.length > 1) {
            const fechasFormateadas = [];
            let inicioRango = fechasNormalizadas[0];
            let finRango = fechasNormalizadas[0];

            for (let i = 1; i < fechasNormalizadas.length; i += 1) {
              const [anioActual, mesActual, diaActual] = fechasNormalizadas[i].split('-');
              const fechaActual = new Date(anioActual, mesActual - 1, diaActual);
              const [anioAnterior, mesAnterior, diaAnterior] = finRango.split('-');
              const fechaAnterior = new Date(anioAnterior, mesAnterior - 1, diaAnterior);

              const diferenciaDias = (fechaActual - fechaAnterior) / (1000 * 60 * 60 * 24);

              if (diferenciaDias === 1) {
                finRango = fechasNormalizadas[i];
              } else {
                if (inicioRango === finRango) {
                  fechasFormateadas.push(inicioRango);
                } else {
                  fechasFormateadas.push(`${inicioRango} hasta ${finRango}`);
                }
                inicioRango = fechasNormalizadas[i];
                finRango = fechasNormalizadas[i];
              }
            }
            if (inicioRango === finRango) {
              fechasFormateadas.push(inicioRango);
            } else {
              fechasFormateadas.push(`${inicioRango} hasta ${finRango}`);
            }
            // eslint-disable-next-line no-param-reassign
            item.serviceDeliveryPeriodicityFormatted = fechasFormateadas;
            // eslint-disable-next-line no-param-reassign
            item.serviceDeliveryPeriodicity = fechasNormalizadas;
            console.log('fechasNormalizadas', fechasNormalizadas);
            console.log('serviceDeliveryPeriodicityFormatted', item.serviceDeliveryPeriodicityFormatted);
          }
        }
      });
      if (this.formData.initiatedFisabs) {
        this.formData.linked = [
          {
            id: this.formData.createdFisabsSelected.id,
            locationType: 'FISABS',
          }
        ];
      }
      return {
        ...this.formData,
        totalSubtotalItemsAmount: formatCurrency(this.totalSubtotalItemsAmount),
        items: [
          ...this.formData.items.map((item, index) => ({
            ...item,
            subtotalAmount: `$${item.subtotalAmount}`,
            index: index + 1,
            serviceDeliveryPeriodicityFormatted: item.serviceDeliveryPeriodicityFormatted,
            serviceDeliveryLocation: item.serviceDeliveryLocation === OTHER_ADDRESS
              ? item.serviceDeliveryLocationCustom
              : item.serviceDeliveryLocation,
            criteriaToSelectProvider: item.criteriaToSelectProvider
              .map((criteria) => (criteria === 'Otro (detallar)' ? `Otro (detallar) ${ item.providerSelectionCriteria}` : criteria)),
          }))
        ]
      };
    },
    async generatePdf(id) {
      showToast(this, 'Generando archivo PDF', 'En camino...');
      const response = await ioGetCustomDocument(this.$route.params.type, id, { pdf: 'si' });
      showToast(this, 'Archivo generado', 'Listo!', { variant: 'success', autoHideDelay: 3000 });
      window.open(response.data.pdf, '_blank');
      setTimeout(() => window.history.back(), 2000);
    },
    async save(isDraft = false) {
      if (!await this.validateForm()) return;

      const from = this.$route.query?.from;
      // TODO PARA QUE OBTENGA EL COMMIT
      this.saving = true;
      try {
        showToast(this, 'Guardando documento', 'En camino...', { variant: 'primary', autoHideDelay: 2000 });
        const payload = {
          type: this.$route.params.type,
          query: {
            ...this.$route.query,
            numerar: isDraft ? 'no' : 'si',
            ...(isDraft && { simular: 'si' }),
            ...(from === 'draft' && { replace: 'si' })
          },
          formData: this.getFormData(),
        };

        const response = await ioCreateCustomDocument(payload);
        await this.notifyCreation(response.data.name || '');
        await this.generatePdf(response.data.document.id);
      } finally {
        this.saving = false;
      }
    },
    async saveDraft() {
      this.saving = true;
      const { from } = this.$route.query;
      try {
        showToast(this, 'Guardando documento', 'En camino...', { variant: 'primary', autoHideDelay: 2000 });
        const payload = {
          type: this.$route.params.type,
          query: {
            ...this.$route.query,
            numerar: 'no',
            ...(from === 'draft' && { replace: 'si' })
          },
          formData: {
            ...this.getFormData(),
            status: '3'
          },
        };

        const response = await ioCreateCustomDocument(payload);
        const docId = response.data?.document?.id || response.data?.documentId;
        if (docId) {
          await this.updateRouteAsDraft(docId);
        }
        showToast(this, 'Borrador guardado correctamente', '¡Listo!', { variant: 'primary', autoHideDelay: 3000 });
      } finally {
        this.saving = false;
      }
    },
    async preview() {
      if (!await this.validateForm()) return;
      this.$refs.pdfPreview.setFormData(this.getFormData());
      await this.$refs.pdfPreview.preview();
    },
    dfsUpdateFiles(files) {
      this.formData.attachments = [...files];
    },
    async loadAttachmentsConfig() {
      this.dfsSelectorsLoading = true;
      try {
        const { selectors } = await getAttachmentsConfig(DOC_TYPE);
        this.dfsSelectors = selectors;
      } finally {
        this.dfsSelectorsLoading = false;
        this.dfsValid = false;
      }
    },
    handleLocationChange(value, item) {
      this.$set(item, 'serviceDeliveryLocationCustom', '');
      this.$set(item, 'serviceDeliveryLocationJustification', '');
    },
    async loadFisabsPage() {
      const PAGE_SIZE = 100;
      const { data } = await ioGetDocuments('FISABS', {
        elementosPorPagina: PAGE_SIZE,
        pagina: this.fisabsPage,
      });
      const filtered = data.documents
        .filter((file) => file.createdBy === this.usr.id && file.status !== '3')
        .map((item) => ({
          id: item.id,
          value: item.id,
          label: `FISABS - N° ${item.numero} - ${Vue.moment(item.createdAt).format('DD-MM-YYYY HH:mm:ss')}`
        }));
      this.createdFisabs = [...this.createdFisabs, ...filtered];
      this.hasMoreFisabs = this.createdFisabs.length < data.totalElementos;
    },
    async loadMoreFisabs() {
      if (this.loadingMoreFisabs || !this.hasMoreFisabs) return;
      this.loadingMoreFisabs = true;
      try {
        this.fisabsPage += 1;
        await this.loadFisabsPage();
      } finally {
        this.loadingMoreFisabs = false;
      }
    },
    calculateSubtotal(item) {
      const itemFunction = { ...item };
      const cleanAmount = itemFunction.serviceAmount
        ? Number(String(itemFunction.serviceAmount).replace(/[^\d]/g, ''))
        : 0;
      const quantity = Number(itemFunction.serviceQuantity) || 0;
      const subtotal = cleanAmount * quantity;

      itemFunction.subtotalAmount = subtotal ? subtotal.toLocaleString('es-CL') : '';
      this.$set(item, 'subtotalAmount', subtotal ? subtotal.toLocaleString('es-CL') : '');
    },
    onAddItem(newItem) {
      if (newItem.serviceAmount || newItem.serviceQuantity) {
        this.calculateSubtotal(newItem);
      }
    },
  },
  watch: {
    'formData.items': {
      handler(newItems) {
        // Calcular subtotales para todos los items cuando cambien
        newItems.forEach((item) => {
          if (item.serviceAmount || item.serviceQuantity) {
            this.calculateSubtotal(item);
          }
        });
      },
      deep: true,
      immediate: true
    }
  },
  async created() {
    this.usr = this.$store.getters['Usuario/currentUser'];
    await this.loadAttachmentsConfig();
    await this.loadFisabsPage();

    // link-catalogo-erp-global-fisabs

    const { data: { opciones } } = await listV2Options({
      grupoOpcion: 'link-catalogo-erp-global-fisabs',
      filtros: ''
    });
    const linkErp = opciones.length ? opciones[0].valor : '';
    this.linkCatalogoErpGlobal = linkErp;
    this.initialItem.linkCatalogoErpGlobal = linkErp;
    this.formData.items[0].linkCatalogoErpGlobal = linkErp;
  },
  mounted() {
    this.formData.requesterName = this.usr.nombre || '';
    this.formData.requesterEmail = this.usr.correo || '';
  },
  computed: {
    totalSubtotalItemsAmount() {
      return this.formData.items.reduce((total, item) => {
        const amount = item.subtotalAmount
          ? Number(item.subtotalAmount.toString().replace(/\D/g, '')) : 0;
        return total + amount;
      }, 0);
    },
    totalItemsAmount() {
      return this.formData.items.reduce((total, item) => {
        const amount = item.serviceAmount
          ? Number(item.serviceAmount.toString().replace(/\D/g, '')) : 0;
        return total + amount;
      }, 0);
    },
  }
};
</script>
<style>
</style>
